import type { AnaliseGrauExposicaoRisco } from "@/types/analise/AnaliseGrauExposicaoRisco";
import type { Rubrica } from "@/types/rubricas/Rubrica";
import type {
  IncidenciasAcaoEmMassa,
  PayloadAcaoEmMassa,
  ProcessamentoLoteAcaoEmMassaRequest,
} from "@/types/rubricas/analise-lote/ProcessamentoLote";
import { descricaoLote } from "@/components/rubricas/processamentoLote";

export const ROTA_ACAO_EM_MASSA = "/esocial/rubricas/acao-em-massa";

/** `location.state` com que a Análise em Lote abre o wizard. */
export interface EstadoAcaoEmMassa {
  rubricas: Rubrica[];
  /** Descrição de origem, ou {@link ORIGEM_SELECAO_MULTIPLA}. */
  origem: string;
}

export const ORIGEM_SELECAO_MULTIPLA = "Seleção múltipla";

export type AcaoCampo = "MANTER" | "SUBSTITUIR" | "LIMPAR";

export const ACOES_CAMPO: { value: AcaoCampo; label: string }[] = [
  { value: "MANTER", label: "Manter como está" },
  { value: "SUBSTITUIR", label: "Substituir todos por" },
  { value: "LIMPAR", label: "Limpar valor" },
];

export const TIPOS_RUBRICA = [
  { value: "1", label: "1 - Vencimento, provento ou pensão" },
  { value: "2", label: "2 - Desconto" },
  { value: "3", label: "3 - Informativa" },
  { value: "4", label: "4 - Informativa dedutora" },
];

/** `tabela` = slug de /configuracoes/tabelas/incidencias/{tabela}. */
export const CAMPOS_INCIDENCIA = [
  { key: "cp", label: "COD INC CP", sigla: "CP", tabela: "inss" },
  { key: "irrf", label: "COD INC IRRF", sigla: "IR", tabela: "irrf" },
  { key: "fgts", label: "COD INC FGTS", sigla: "FG", tabela: "fgts" },
  { key: "cprp", label: "COD INC CPRP", sigla: "CPRP", tabela: "cprp" },
  { key: "pis", label: "COD INC PIS", sigla: "PIS", tabela: "pispasep" },
] as const;

export type CampoIncidencia = (typeof CAMPOS_INCIDENCIA)[number]["key"];

/** Valores exibidos nos selects de incidência; "" = manter o valor atual de cada rubrica. */
export type ValoresIncidencia = Record<CampoIncidencia | "teto", string>;

export const OPCOES_TETO = ["Sim", "Não"];

export interface CampoEdicao {
  acao: AcaoCampo;
  valor: string;
}

export interface EdicaoAcaoEmMassa {
  tipo: CampoEdicao;
  natureza: CampoEdicao;
  /**
   * null = automático (segue a natureza recomendada); preenchido = exceção manual. Guarda só os
   * campos editados: o pré-preenchimento muda com as rubricas e a natureza escolhidas.
   */
  incidencias: Partial<ValoresIncidencia> | null;
  grauRisco: AnaliseGrauExposicaoRisco | null;
  /** Chaves de {@link chaveNatureza} retiradas da ação. */
  naturezasRemovidas: string[];
  comentario: string;
}

export const EDICAO_INICIAL: EdicaoAcaoEmMassa = {
  tipo: { acao: "MANTER", valor: "" },
  natureza: { acao: "MANTER", valor: "" },
  incidencias: null,
  grauRisco: null,
  naturezasRemovidas: [],
  comentario: "",
};

/** Linha de /configuracoes/rubricas/incidencia-natureza (tabela eSocial). */
export interface NaturezaCatalogo {
  codigo: string;
  nome: string;
  tipo: string;
  cp: string;
  irrf: string;
  fgts: string;
  cprp: string;
  pisPasep: string;
  teto: string;
}

const VAZIO = "—";

export function formatarCompetencia(value: string | null): string {
  if (value && /^\d{4}-(0[1-9]|1[0-2])$/.test(value)) {
    return `${value.slice(5)}/${value.slice(0, 4)}`;
  }
  return value || VAZIO;
}

/** "1 - Vencimento…" → "1"; nomes sem código ficam inteiros. */
export function formatarTipo(tipoNome: string | null): string {
  return tipoNome?.split(" - ")[0]?.trim() || VAZIO;
}

/** A tabela de naturezas pode listar vários códigos permitidos ("53, 54"); o 1º é o recomendado. */
export function primeiroCodigo(lista?: string | null): string {
  return (lista ?? "").split(",")[0]?.trim() ?? "";
}

export function chaveNatureza(rubrica: Rubrica): string {
  return `${rubrica.descricao ?? ""}::${rubrica.naturezaCodigo ?? ""}`;
}

/** Valor compartilhado por todos os itens, ou undefined quando divergem (ou a lista é vazia). */
export function valorComum<T>(valores: T[]): T | undefined {
  if (!valores.length) return undefined;
  return valores.every((valor) => valor === valores[0]) ? valores[0] : undefined;
}

export function removerNaturezas(rubricas: Rubrica[], chaves: string[]): Rubrica[] {
  if (!chaves.length) return rubricas;
  const removidas = new Set(chaves);
  return rubricas.filter((rubrica) => !removidas.has(chaveNatureza(rubrica)));
}

export function incidenciasDaNatureza(natureza: NaturezaCatalogo): ValoresIncidencia {
  return {
    cp: primeiroCodigo(natureza.cp),
    irrf: primeiroCodigo(natureza.irrf),
    fgts: primeiroCodigo(natureza.fgts),
    cprp: primeiroCodigo(natureza.cprp),
    pis: primeiroCodigo(natureza.pisPasep),
    teto: OPCOES_TETO.includes(natureza.teto) ? natureza.teto : "",
  };
}

/** Pré-carrega com o valor comum das rubricas; o teto não vem na listagem de itens. */
export function incidenciasAtuais(rubricas: Rubrica[]): ValoresIncidencia {
  const comum = (campo: CampoIncidencia) =>
    valorComum(rubricas.map((rubrica) => rubrica[campo] ?? "")) ?? "";
  return {
    cp: comum("cp"),
    irrf: comum("irrf"),
    fgts: comum("fgts"),
    cprp: comum("cprp"),
    pis: comum("pis"),
    teto: "",
  };
}

export function naturezaSubstituta(
  edicao: EdicaoAcaoEmMassa,
  naturezas: NaturezaCatalogo[],
): NaturezaCatalogo | undefined {
  if (edicao.natureza.acao !== "SUBSTITUIR") return undefined;
  return naturezas.find((natureza) => natureza.codigo === edicao.natureza.valor);
}

/** O que os selects mostram antes de uma edição manual. */
export function incidenciasRecomendadas(
  rubricas: Rubrica[],
  edicao: EdicaoAcaoEmMassa,
  naturezas: NaturezaCatalogo[],
): ValoresIncidencia {
  const natureza = naturezaSubstituta(edicao, naturezas);
  return natureza ? incidenciasDaNatureza(natureza) : incidenciasAtuais(rubricas);
}

function incidenciasPreenchidas(
  valores: Partial<ValoresIncidencia>,
): IncidenciasAcaoEmMassa | undefined {
  const incidencias: IncidenciasAcaoEmMassa = {};
  CAMPOS_INCIDENCIA.forEach(({ key }) => {
    if (valores[key]) incidencias[key] = valores[key];
  });
  if (valores.teto) incidencias.teto = valores.teto === "Sim";
  return Object.keys(incidencias).length ? incidencias : undefined;
}

function valorCampo(campo: CampoEdicao): string | null | undefined {
  if (campo.acao === "SUBSTITUIR") return campo.valor;
  if (campo.acao === "LIMPAR") return null;
  return undefined;
}

/** O que os selects de incidência mostram: o recomendado com as edições manuais por cima. */
export function incidenciasExibidas(
  edicao: EdicaoAcaoEmMassa,
  recomendadas: ValoresIncidencia,
): ValoresIncidencia {
  return { ...recomendadas, ...edicao.incidencias };
}

/**
 * Payload de ACAO_EM_MASSA: só leva o que muda. Ao trocar a natureza, as incidências recomendadas
 * da nova natureza vão junto (com as exceções manuais por cima); sem troca, só as editadas — o
 * worker mantém as antigas se omitidas.
 */
export function montarPayload(
  edicao: EdicaoAcaoEmMassa,
  recomendadas: ValoresIncidencia,
): PayloadAcaoEmMassa {
  const payload: PayloadAcaoEmMassa = {};
  const tipo = valorCampo(edicao.tipo);
  const natureza = valorCampo(edicao.natureza);
  if (tipo !== undefined) payload.tipo = tipo;
  if (natureza !== undefined) payload.natureza = natureza;

  const valores =
    edicao.natureza.acao === "SUBSTITUIR"
      ? incidenciasExibidas(edicao, recomendadas)
      : edicao.incidencias;
  const incidencias = valores ? incidenciasPreenchidas(valores) : undefined;
  if (incidencias) payload.incidencias = incidencias;

  if (edicao.grauRisco) payload.grauRisco = edicao.grauRisco;
  return payload;
}

export function temAlteracoes(payload: PayloadAcaoEmMassa): boolean {
  return Object.keys(payload).length > 0;
}

export function camposAlterados(payload: PayloadAcaoEmMassa, edicao: EdicaoAcaoEmMassa): string[] {
  return [
    "tipo" in payload && "Tipo",
    "natureza" in payload && "Natureza",
    payload.incidencias && (edicao.incidencias ? "Incidências (exceção)" : "Incidências"),
    payload.grauRisco && "Grau de risco (exceção manual)",
  ].filter((campo): campo is string => !!campo);
}

/** O que falta preencher para salvar a etapa de campos afetados. */
export function pendenciasEdicao(edicao: EdicaoAcaoEmMassa): string[] {
  return [
    edicao.tipo.acao === "SUBSTITUIR" && !edicao.tipo.valor && "Selecione o novo tipo.",
    edicao.natureza.acao === "SUBSTITUIR" && !edicao.natureza.valor && "Selecione a nova natureza.",
  ].filter((pendencia): pendencia is string => !!pendencia);
}

export function requisicaoAcaoEmMassa(
  rubricas: Rubrica[],
  payload: PayloadAcaoEmMassa,
  comentario: string,
): ProcessamentoLoteAcaoEmMassaRequest {
  return {
    acao: "ACAO_EM_MASSA",
    ...descricaoLote(comentario),
    idsAnaliseRubrica: rubricas.map((rubrica) => rubrica.idAnaliseRubrica),
    payload,
  };
}

export interface NaturezaAfetada {
  chave: string;
  codigo: number | null;
  nome: string | null;
  rubricas: Rubrica[];
}

export interface GrupoAfetado {
  descricao: string;
  naturezas: NaturezaAfetada[];
}

/** Painel "Rubricas Afetadas": descrição → naturezas distintas (rubricas iguais contam uma vez). */
export function agruparRubricasAfetadas(rubricas: Rubrica[]): GrupoAfetado[] {
  const grupos = new Map<string, Map<string, NaturezaAfetada>>();
  rubricas.forEach((rubrica) => {
    const descricao = rubrica.descricao ?? VAZIO;
    const naturezas = grupos.get(descricao) ?? new Map<string, NaturezaAfetada>();
    grupos.set(descricao, naturezas);
    const chave = chaveNatureza(rubrica);
    const natureza = naturezas.get(chave) ?? {
      chave,
      codigo: rubrica.naturezaCodigo,
      nome: rubrica.naturezaNome,
      rubricas: [],
    };
    natureza.rubricas.push(rubrica);
    naturezas.set(chave, natureza);
  });
  return [...grupos.entries()].map(([descricao, naturezas]) => ({
    descricao,
    naturezas: [...naturezas.values()].sort((a, b) => (a.codigo ?? 0) - (b.codigo ?? 0)),
  }));
}

export interface AlteracaoValor {
  antigo: string;
  /** undefined = mantido; string = novo valor ("—" quando limpo). */
  novo?: string;
}

export interface LinhaRevisao {
  rubrica: Rubrica;
  tipo: AlteracaoValor;
  natureza: AlteracaoValor;
  incidencias: (AlteracaoValor & { sigla: string })[];
}

function alteracao(antigo: string, novo: string | null | undefined): AlteracaoValor {
  if (novo === undefined) return { antigo };
  return { antigo, novo: novo ?? VAZIO };
}

/** De-para (antigo → novo) exibido na revisão, calculado a partir do payload. */
export function linhaRevisao(rubrica: Rubrica, payload: PayloadAcaoEmMassa): LinhaRevisao {
  const incidencias = payload.incidencias ?? {};
  const alteradas: LinhaRevisao["incidencias"] = CAMPOS_INCIDENCIA.flatMap(({ key, sigla }) => {
    const antigo = rubrica[key] || VAZIO;
    const novo = incidencias[key];
    return novo && novo !== antigo ? [{ sigla, antigo, novo }] : [];
  });
  // O teto atual não vem na listagem de itens, então só o novo valor é conhecido.
  if (typeof incidencias.teto === "boolean") {
    alteradas.push({ sigla: "Teto", antigo: VAZIO, novo: incidencias.teto ? "Sim" : "Não" });
  }
  return {
    rubrica,
    tipo: alteracao(formatarTipo(rubrica.tipoNome), payload.tipo),
    natureza: alteracao(String(rubrica.naturezaCodigo ?? VAZIO), payload.natureza),
    incidencias: alteradas.length
      ? alteradas
      : CAMPOS_INCIDENCIA.slice(0, 3).map(({ key, sigla }) => ({
          sigla,
          antigo: rubrica[key] || VAZIO,
        })),
  };
}
